import { CitiesListSection } from 'components/city-blocks/cities-list-section';
import { HeaderCopy } from 'components/city-blocks/header-copy';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import { generateBreadcrumbSchema, generateLocationListSchema, SchemaMarkup, SEOHead } from 'components/seo';
import { mapValues } from 'lodash';
import React from 'react';
import { NoFollowLink } from 'shared/no-follow-link';
import { fetchCitiesData } from 'utils/api-utils';
import { getAssetPath } from 'utils/asset-utils';
import { citiesRootBreadcrumbs } from 'utils/breadcrumb-util';
import { LINKS_BLOCK_LIMIT } from 'utils/constants';
import { getFrontEndUrl } from 'utils/environment-util';
import { RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { memoizedCitiesByRegionId, memoizedCityPageCities, memoizedRegionsById } from 'utils/memoized-data-structure';
import { reduceShallowCityPage, ShallowCityPage } from 'utils/types';

const CITY_HOME_META_DESC =
  'Shef delivers authentic homemade food to cities across the US. Find local chefs and authentic cuisines in your city. Search our complete list of food delivery cities!';
const HEADER_COPY_TITLE = 'Find a Local Delivery or Pickup Option Near You For Delicious Homemade Food!';

const invokeHeaderCopyContent = ({ numCities }: { numCities: number }): React.ReactElement => (
  <span>
    Shef delivers traditional homemade food to over {numCities} cities throughout the US and has food pickup in many
    more! With thousands of local Shefs & dishes to choose from, you're sure to find the perfect homemade dish for
    breakfast, lunch, or dinner! Browse by metro, city, or{' '}
    <NoFollowLink
      href={`${getFrontEndUrl()}`}
      className='text-neutral-500 underline underline-offset-2 hover:text-neutral-700'>
      search by ZIP
    </NoFollowLink>
  </span>
);

interface IProps {
  cityPages: ShallowCityPage[];
  citiesByRegion: { [identifier: string]: ShallowCityPage[] };
  regionsById: { [identifier: string]: RegionInfoFragment };
}

const CitiesWeServe: React.FC<IProps> = ({ cityPages }: IProps) => {
  const title = 'See All US Cities Where Our Shefs Cook Homemade Food!';
  const heroText = 'Cities Where Shef Delivers';
  const siteUrl = getFrontEndUrl();
  const pageUrl = `${siteUrl}/cities-we-serve`;

  const schemas = [
    generateBreadcrumbSchema([
      { name: 'Home', url: siteUrl },
      { name: 'Cities We Serve', url: pageUrl },
    ]),
    generateLocationListSchema(cityPages, 'city', siteUrl),
  ];

  return (
    <Layout>
      <SEOHead
        title={title}
        description={CITY_HOME_META_DESC}
        url={pageUrl}
        keywords={[
          'cities where shef delivers',
          'food delivery cities',
          'homemade food delivery cities',
          'local chef delivery cities',
          'authentic food delivery locations',
        ]}
        image={`${siteUrl}/images/CDN/cities_we_serve_bg.png`}
        canonical={pageUrl}
      />
      <SchemaMarkup schemas={schemas} id='cities-schema' />
      <HeroBlock
        className='mb-10'
        heroText={heroText}
        crumbs={citiesRootBreadcrumbs}
        backgroundImage={getAssetPath('/images/CDN/cities_we_serve_bg.png')}
      />
      <MaxWidthContainer className='px-4 md:px-0'>
        <HeaderCopy
          className='mb-12'
          title={HEADER_COPY_TITLE}
          content={invokeHeaderCopyContent({ numCities: cityPages.length })}
        />
      </MaxWidthContainer>

      <MaxWidthContainer className='px-4 md:px-0'>
        <CitiesListSection title='All cities Shef serves' cities={cityPages} />
      </MaxWidthContainer>
    </Layout>
  );
};

export async function getStaticProps() {
  const apiData = await fetchCitiesData();

  if (apiData?.cityPages && apiData?.citiesByRegion && apiData?.regionsById) {
    return {
      props: {
        cityPages: apiData.cityPages,
        citiesByRegion: apiData.citiesByRegion,
        regionsById: apiData.regionsById,
      },
    };
  }
  const cityPageCities = await memoizedCityPageCities();
  const memoizedCitiesByRegion = await memoizedCitiesByRegionId();
  const regionsById = await memoizedRegionsById();

  const cityPages = cityPageCities
    .map(reduceShallowCityPage)
    .sort((a, b) => a.stateNameShort.localeCompare(b.stateNameShort) || a.cityName.localeCompare(b.cityName));
  const citiesByRegion = mapValues(memoizedCitiesByRegion, (array) => array.slice(0, LINKS_BLOCK_LIMIT));

  return {
    props: {
      cityPages,
      citiesByRegion,
      regionsById,
    },
  };
}

export default CitiesWeServe;
